import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Star, ShoppingBag, Clock, Menu, Settings, Repeat } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import ActiveOrders from '@/components/business/ActiveOrders';
import MenuManagement from '@/components/business/MenuManagement';
import BusinessHours from '@/components/business/BusinessHours';
import BusinessSettings from '@/components/business/BusinessSettings';
import CompletedOrdersView from '@/components/business/CompletedOrdersView';
import YearlySalesView from '@/components/business/YearlySalesView';
import RestaurantRatingsView from '@/components/business/RestaurantRatingsView';
import { VendorSwapRequests } from '@/components/business/VendorSwapRequests';
import { useBusinessSoundNotification } from '@/hooks/useBusinessSoundNotification';
import { useOrderPolling } from '@/hooks/useOrderPolling';
import { useRealtimeOrderNotifications } from '@/hooks/useRealtimeOrderNotifications';

interface DashboardStats {
  ordersToday: number;
  totalSales: number;
  rating: number;
}

interface Restaurant {
  id: string;
  name: string;
  rating: number;
  business_type: string;
  categories?: string[];
}

const BusinessDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    ordersToday: 0,
    totalSales: 0,
    rating: 0.0
  });
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'dashboard' | 'orders' | 'menu' | 'hours' | 'settings' | 'completed-orders' | 'yearly-sales' | 'ratings' | 'swap-requests'>('dashboard');

  // Initialize sound notifications for this restaurant
  useBusinessSoundNotification(restaurant?.id || '');

  // Initialize order polling for sound notifications
  const { isPolling, manualCheck, lastOrderCount } = useOrderPolling(restaurant?.id || '');

  // Initialize realtime push notifications
  useRealtimeOrderNotifications();

  // Add effect to initialize audio on component mount (important for mobile)
  useEffect(() => {
    // Add a click listener to initialize audio on first user interaction
    const initializeAudioOnClick = () => {
      console.log('🔊 Initializing audio on user interaction...');
      // This will help unlock audio on mobile devices
    };

    document.addEventListener('touchstart', initializeAudioOnClick, { once: true });
    document.addEventListener('click', initializeAudioOnClick, { once: true });

    return () => {
      document.removeEventListener('touchstart', initializeAudioOnClick);
      document.removeEventListener('click', initializeAudioOnClick);
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchRestaurantData();
    }
  }, [user]);

  const fetchRestaurantData = async () => {
    try {
      // Fetch restaurant data
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('restaurants')
        .select('id, name, rating, business_type, categories')
        .eq('vendor_id', user?.id)
        .single();

      if (restaurantError) {
        console.error('Error fetching restaurant:', restaurantError);
        return;
      }

      setRestaurant(restaurantData);

      if (restaurantData) {
        // Fetch today's completed orders (both delivery and pickup)
        const today = new Date().toISOString().split('T')[0];
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('id, total_amount, delivery_fee, created_at, completed_at, delivery_type')
          .eq('restaurant_id', restaurantData.id)
          .gte('completed_at', `${today}T00:00:00`)
          .lt('completed_at', `${today}T23:59:59`)
          .not('completed_at', 'is', null);

        if (ordersError) {
          console.error('Error fetching orders:', ordersError);
        } else {
          const ordersToday = ordersData?.length || 0;

          // Fetch yearly sales (excluding delivery fees for vendor view)
          const currentYear = new Date().getFullYear();
          const { data: yearlySalesData, error: salesError } = await supabase
            .from('orders')
            .select('total_amount, delivery_fee')
            .eq('restaurant_id', restaurantData.id)
            .gte('completed_at', `${currentYear}-01-01T00:00:00`)
            .lt('completed_at', `${currentYear + 1}-01-01T00:00:00`)
            .not('completed_at', 'is', null);

          // Calculate total sales excluding delivery fees
          const totalSales = salesError ? 0 : (yearlySalesData?.reduce((sum, order) => {
            const orderTotal = Number(order.total_amount);
            const deliveryFee = Number(order.delivery_fee) || 0;
            return sum + (orderTotal - deliveryFee);
          }, 0) || 0);

          // Fetch average rating
          const { data: ratingsData, error: ratingsError } = await supabase
            .from('ratings')
            .select('restaurant_rating')
            .eq('restaurant_id', restaurantData.id)
            .not('restaurant_rating', 'is', null);

          let averageRating = 0;
          if (!ratingsError && ratingsData && ratingsData.length > 0) {
            const sum = ratingsData.reduce((total, rating) => total + rating.restaurant_rating, 0);
            averageRating = sum / ratingsData.length;
          }

          setStats({
            ordersToday,
            totalSales,
            rating: averageRating
          });
        }
      }
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, onClick }: { 
    icon: any, 
    title: string, 
    value: string,
    color: string,
    onClick?: () => void
  }) => (
    <Card className={`min-w-0 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
            <p className="text-lg sm:text-2xl font-bold truncate">{value}</p>
          </div>
          <Icon className={`h-6 w-6 sm:h-8 sm:w-8 ${color} flex-shrink-0`} />
        </div>
      </CardContent>
    </Card>
  );

  const MenuButton = ({ icon: Icon, title, description, onClick, isActive }: {
    icon: any,
    title: string,
    description: string,
    onClick: () => void,
    isActive?: boolean
  }) => (
    <Card className={`cursor-pointer hover:shadow-md transition-shadow ${isActive ? 'ring-2 ring-red-500' : ''}`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm sm:text-base">{title}</h3>
            <p className="text-xs sm:text-sm text-gray-600">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md w-full">
          <h2 className="text-xl font-semibold mb-2">No Restaurant Found</h2>
          <p className="text-gray-600 mb-4">You need to have an approved vendor application first.</p>
          <Button onClick={() => navigate('/vendor-onboarding')} className="w-full">
            Apply as Vendor
          </Button>
        </Card>
      </div>
    );
  }

  // Handle specific stat views
  if (activeView === 'completed-orders') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveView('dashboard')}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold truncate">Today's Completed Orders</h1>
          </div>
        </div>
        <div className="p-4">
          <CompletedOrdersView restaurantId={restaurant.id} />
        </div>
      </div>
    );
  }

  if (activeView === 'yearly-sales') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveView('dashboard')}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold truncate">Yearly Sales</h1>
          </div>
        </div>
        <div className="p-4">
          <YearlySalesView restaurantId={restaurant.id} />
        </div>
      </div>
    );
  }

  if (activeView === 'ratings') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveView('dashboard')}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold truncate">Customer Ratings</h1>
          </div>
        </div>
        <div className="p-4">
          <RestaurantRatingsView restaurantId={restaurant.id} />
        </div>
      </div>
    );
  }

  if (activeView !== 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveView('dashboard')}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <h1 className="text-lg sm:text-xl font-semibold truncate">
              {activeView === 'orders' && 'Active Orders'}
              {activeView === 'menu' && (restaurant.business_type === 'marketplace' ? 'Stock Management' : 'Menu Management')}
              {activeView === 'hours' && 'Business Hours'}
              {activeView === 'settings' && 'Business Settings'}
              {activeView === 'swap-requests' && 'Device Swap Requests'}
            </h1>
          </div>
        </div>

        <div className="p-4">
          {activeView === 'orders' && <ActiveOrders restaurantId={restaurant.id} />}
          {activeView === 'menu' && <MenuManagement restaurantId={restaurant.id} businessType={restaurant.business_type} />}
          {activeView === 'hours' && <BusinessHours restaurantId={restaurant.id} />}
          {activeView === 'settings' && <BusinessSettings restaurantId={restaurant.id} onUpdate={fetchRestaurantData} businessType={restaurant.business_type} />}
          {activeView === 'swap-requests' && <VendorSwapRequests restaurantId={restaurant.id} />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home')}
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold truncate">Business Dashboard</h1>
            <p className="text-sm text-gray-600 truncate">{restaurant.name}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={ShoppingBag}
            title="Orders Today"
            value={stats.ordersToday.toString()}
            color="text-blue-600"
            onClick={() => setActiveView('completed-orders')}
          />
          <StatCard
            icon={TrendingUp}
            title="Yearly Sales"
            value={`D${stats.totalSales.toFixed(2)}`}
            color="text-green-600"
            onClick={() => setActiveView('yearly-sales')}
          />
          <StatCard
            icon={Star}
            title="Rating"
            value={stats.rating > 0 ? stats.rating.toFixed(1) : 'No ratings'}
            color="text-yellow-600"
            onClick={() => setActiveView('ratings')}
          />
        </div>

        {/* Menu Options */}
        <div className="space-y-4">
          <MenuButton
            icon={ShoppingBag}
            title="Active Orders"
            description="View and manage current orders"
            onClick={() => setActiveView('orders')}
          />
          <MenuButton
            icon={Menu}
            title={restaurant.business_type === 'marketplace' ? 'Stock Items' : 'Menu Items'}
            description={restaurant.business_type === 'marketplace' ? 'Add and edit your stock' : 'Add and edit your menu'}
            onClick={() => setActiveView('menu')}
          />
          <MenuButton
            icon={Clock}
            title="Business Hours"
            description="Set your opening hours"
            onClick={() => setActiveView('hours')}
          />
          <MenuButton
            icon={Settings}
            title="Settings"
            description="Manage business settings"
            onClick={() => setActiveView('settings')}
          />
          {restaurant.categories?.includes('technology') && (
            <MenuButton
              icon={Repeat}
              title="Device Swap Requests"
              description="Manage device trade-in requests"
              onClick={() => setActiveView('swap-requests')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessDashboardPage;
